import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { ensureSession } from '@/features/auth/api';
import { orgQuery } from '@/features/org/api';
import { pickOrgId, session } from '@/lib/auth/session';
import type { OAuthResult } from '@/lib/nav-state';
import { takeOAuthReturnPath } from '@/lib/oauth-flow';

/**
 * Where the backend's connector OAuth callback sends the browser:
 * /connector-setup?success=<provider> or ?error=<provider>&reason=<slug> (+ warning).
 * The result moves into history state and the user goes on to the page that connects
 * data: Connect Data while the org is onboarding, else Settings → Integrations (the page
 * the flow started from wins when it is one of those two). Nothing renders here.
 */
export const Route = createFileRoute('/connector-setup')({
  validateSearch: z.object({
    success: z.string().optional(),
    error: z.string().optional(),
    reason: z.string().optional(),
    warning: z.string().optional(),
  }),
  beforeLoad: async ({ search, location, context }) => {
    const s = await ensureSession();
    if (!s.user) throw redirect({ to: '/sign-in', state: { next: location.href } });
    if (!s.user.email_verified) throw redirect({ to: '/verify-email', state: { email: s.user.email } });
    const orgId = pickOrgId(s.user);
    session.set({ orgId });

    const oauthResult: OAuthResult = {};
    if (search.success) oauthResult.success = search.success;
    if (search.error) oauthResult.error = search.error;
    if (search.reason) oauthResult.reason = search.reason;
    if (search.warning) oauthResult.warning = search.warning;

    let onboarded = true;
    try {
      onboarded = (await context.queryClient.query({ ...orgQuery(orgId), staleTime: 'static' }))
        .onboarding_completed;
    } catch {
      // The org read failed: fall through to Integrations, which shows its own error state.
    }
    const returnTo = takeOAuthReturnPath();
    const target =
      returnTo === '/onboarding/connect-data' || returnTo === '/settings/integrations'
        ? returnTo
        : onboarded
          ? '/settings/integrations'
          : '/onboarding/connect-data';
    throw redirect({ to: target, replace: true, state: { oauthResult } });
  },
  component: () => null,
});
